import { defineStore } from 'pinia';
import {
  getAcknowledgementsConfig,
  normalizeAcknowledgementsConfig,
  updateAcknowledgementsConfig,
} from '@/api/settings';
import { handleKnownSettingsApiErrors } from '@/stores/settings/errorHandling';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';
import { useAcknowledgementSettingsForm } from '@/stores/settings/acknowledgementSettingsForm';

export const useAcknowledgementSettings = defineStore('acknowledgementSettings', () => {
  const form = useAcknowledgementSettingsForm();
  const { setLoading } = useAppLoading();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  const handleValidationError = (error) => {
    const { response } = error;

    if (response && response.code === 'validation_failed') {
      form.setFormValues(normalizeAcknowledgementsConfig(response.attributes || {}));
      form.setBackendErrors(response.errors);
      return true;
    }

    return false;
  };

  const handleError = (error) => {
    const isKnownError = handleKnownSettingsApiErrors({ context: 'Acknowledgements settings', error });

    if (!isKnownError) {
      addErrorSnackbar({ text: `Acknowledgements settings - ${error.message}` });
      throw error;
    }
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });
      const data = await getAcknowledgementsConfig();
      form.setFormValues(data);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const update = async () => {
    if (!form.isFormValid) {
      return;
    }

    try {
      setLoading({ componentLoading: true });

      const data = await updateAcknowledgementsConfig(form.values);

      if (data.success && data.attributes) {
        form.setFormValues(data.attributes);
        addSuccessSnackbar({
          text: 'Acknowledgements settings have been updated',
        });
      }
    } catch (error) {
      if (!handleValidationError(error)) {
        handleError(error);
      }
    } finally {
      setLoading({ componentLoading: false });
    }
  };

  return { load, update };
});
